/**
 * Stellar API Client
 * 
 * Fetch-based API wrapper with JWT authentication.
 * Replaces the Rails CSRF token approach with Bearer token auth.
 */

// Use empty string so all API calls go through Next.js /api/* proxy (avoids CORS)
const API_URL = '';

interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private token: string | null = null;

  /**
   * Set the authentication token
   */
  setToken(token: string | null) {
    this.token = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('stellar_token', token);
        document.cookie = `stellar_token=${token}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      } else {
        localStorage.removeItem('stellar_token');
        document.cookie = 'stellar_token=; path=/; max-age=0';
      }
    }
  }

  /**
   * Get the authentication token
   */
  getToken(): string | null {
    if (this.token) return this.token;
    if (typeof window !== 'undefined') {
      return localStorage.getItem('stellar_token');
    }
    return null;
  }

  /**
   * Build request headers
   */
  private getHeaders(includeContentType = true): HeadersInit {
    const headers: HeadersInit = {
      Accept: 'application/json',
    };

    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error: ApiError = {
        message: 'An error occurred',
        status: response.status,
      };

      try {
        const data = await response.json();
        error.message = data.error || data.message || error.message;
        error.errors = data.errors;
      } catch {
        error.message = response.statusText;
      }

      // Handle 401 - unauthorized
      if (response.status === 401) {
        this.setToken(null);
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }

      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    let url = `${API_URL}${endpoint}`;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(`${key}[]`, String(v)));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * POST with FormData (for file uploads)
   */
  async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(false), // Don't set Content-Type for FormData
      body: formData,
    });

    return this.handleResponse<T>(response);
  }
}

// Singleton instance
export const api = new ApiClient();

// =============================================================================
// API Resource Classes
// =============================================================================

import type { 
  AuthUser, 
  LoginCredentials, 
  SignupData,
  Task,
  Feature,
  View,
  Insight,
  User,
  Property,
  PaginatedResponse,
} from '@/types';

/**
 * Auth API
 */
export const auth = {
  async login(credentials: LoginCredentials): Promise<{ user: AuthUser; token: string }> {
    const response = await api.post<{ user: AuthUser; token: string }>('/api/v1/auth/login', credentials);
    api.setToken(response.token);
    return response;
  },

  async signup(data: SignupData): Promise<{ user: AuthUser; token: string }> {
    const response = await api.post<{ user: AuthUser; token: string }>('/api/v1/auth/signup', data);
    api.setToken(response.token);
    return response;
  },

  async logout(): Promise<void> {
    await api.delete('/api/v1/auth/logout');
    api.setToken(null);
  },

  async getProfile(): Promise<AuthUser> {
    return api.get<AuthUser>('/api/v1/profile');
  },

  async getRole(): Promise<{ role: string }> {
    return api.get<{ role: string }>('/api/v1/profile/role');
  },
};

/**
 * Tasks API (renamed from Features)
 */
export const tasks = {
  async list(params?: {
    page?: number;
    sort?: { field_id: string; direction: string };
    filters?: Array<{ property_id: string; operator: string; value: unknown }>;
  }): Promise<PaginatedResponse<Task> & { users: User[] }> {
    return api.get('/api/v1/tasks', params);
  },

  async get(id: string | number): Promise<Task> {
    return api.get(`/api/v1/tasks/${id}`);
  },

  async create(data: Partial<Task>): Promise<Task> {
    return api.post('/api/v1/tasks', { task: data });
  },

  async update(id: string | number, data: Partial<Task>): Promise<Task> {
    return api.put(`/api/v1/tasks/${id}`, { task: data });
  },

  async delete(id: string | number): Promise<void> {
    return api.delete(`/api/v1/tasks/${id}`);
  },

  async getActivities(id: string | number): Promise<unknown[]> {
    return api.get(`/api/v1/tasks/${id}/activities`);
  },
};

// Alias for backwards compatibility
export const features = tasks;

/**
 * Views API
 */
export const views = {
  async list(): Promise<View[]> {
    return api.get('/api/v1/views');
  },

  async get(id: string | number): Promise<View> {
    return api.get(`/api/v1/views/${id}`);
  },

  async create(data: Partial<View>): Promise<View> {
    return api.post('/api/v1/views', { view: data });
  },

  async update(id: string | number, data: Partial<View>): Promise<View> {
    return api.put(`/api/v1/views/${id}`, { view: data });
  },

  async delete(id: string | number): Promise<void> {
    return api.delete(`/api/v1/views/${id}`);
  },

  async favorite(id: string | number, favorited: boolean): Promise<View> {
    return api.post(`/api/v1/views/${id}/favorite`, { favorited });
  },
};

/**
 * Insights API
 */
export const insights = {
  async list(params?: {
    page?: number;
    status?: string;
    source?: string;
  }): Promise<PaginatedResponse<Insight>> {
    return api.get('/api/v1/insights', params);
  },

  async get(id: string | number): Promise<Insight> {
    return api.get(`/api/v1/insights/${id}`);
  },

  async create(data: Partial<Insight>): Promise<Insight> {
    return api.post('/api/v1/insights', { insight: data });
  },

  async update(id: string | number, data: Partial<Insight>): Promise<Insight> {
    return api.put(`/api/v1/insights/${id}`, { insight: data });
  },

  async delete(id: string | number): Promise<void> {
    return api.delete(`/api/v1/insights/${id}`);
  },
};

/**
 * Properties API
 */
export const properties = {
  async list(): Promise<Property[]> {
    return api.get('/api/v1/properties');
  },

  async create(data: Partial<Property>): Promise<Property> {
    return api.post('/api/v1/properties', { property: data });
  },

  async update(id: string | number, data: Partial<Property>): Promise<Property> {
    return api.put(`/api/v1/properties/${id}`, { property: data });
  },

  async delete(id: string | number): Promise<void> {
    return api.delete(`/api/v1/properties/${id}`);
  },
};

/**
 * Users/People API
 */
export const people = {
  async list(): Promise<User[]> {
    return api.get('/api/v1/people');
  },

  async get(id: string | number): Promise<User> {
    return api.get(`/api/v1/people/${id}`);
  },

  async create(data: Partial<User>): Promise<User> {
    return api.post('/api/v1/people', { person: data });
  },

  async update(id: string | number, data: Partial<User>): Promise<User> {
    return api.put(`/api/v1/people/${id}`, { person: data });
  },

  async delete(id: string | number): Promise<void> {
    return api.delete(`/api/v1/people/${id}`);
  },
};

export default api;
